import React, { useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { Link } from 'react-router-dom';
import HeaderSection from '../../../components/layout/header';
import { CONFIG } from '../../../shared/constants/common';
import { ROUTES } from '../../../shared/constants/routes.js';

const TABLE_OF_CONTENTS = [
  { id: 'overview', label: 'JuicyHouse.com Content Creator Agreement' },
  { id: 'platform-enrollment', label: 'Platform Enrollment' },
  { id: 'company-proprietary-rights', label: 'Company Proprietary Rights; Limited License' },
  { id: 'creator-account', label: 'Creator Account' },
  { id: 'creator-profile-offerings', label: 'Creator Profile and Offerings' },
  { id: 'relationship-parties', label: 'Relationship of the Parties' },
  { id: 'creator-compensation', label: 'Creator Compensation' },
  { id: 'statements-of-fact', label: 'Statements of Fact' },
  { id: 'privacy', label: 'Privacy' },
  { id: 'no-disparaging', label: 'No-Disparaging' },
  { id: 'termination', label: 'Termination' },
  { id: 'warranty-disclaimers', label: 'Warranty Disclaimers' },
  { id: 'limitation-liability', label: 'Limitation of Liability' },
  { id: 'waiver-california-civil-code', label: 'Waiver of California Civil Code Section 1542' },
  { id: 'scope-disclaimers-limitations', label: 'Scope of Disclaimers and Limitations' },
  { id: 'indemnification', label: 'Indemnification' },
  { id: 'dispute-resolution', label: 'Dispute Resolution' },
  { id: 'general', label: 'General' },
];

const ModernContentCreatorAgreement = ({ history }) => {
  const scrollToSection = useCallback(
    (e, id) => {
      if (e) e.preventDefault();
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const newHash = `#${id}`;
        if (history.location.hash !== newHash) {
          history.replace(history.location.pathname + newHash);
        }
      }
    },
    [history]
  );

  // Scroll to section on mount when URL has a hash (e.g. direct link or refresh)
  useEffect(() => {
    const hash = history.location.hash;
    if (hash) {
      const id = hash.slice(1);
      if (TABLE_OF_CONTENTS.some((item) => item.id === id)) {
        const element = document.getElementById(id);
        if (element) {
          const timer = setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [history.location.hash]);

  return (
    <div className="modern-legal-page">
      {/* Header */}
      <HeaderSection
        type="text"
        title="Content Creator Agreement"
      />

      {/* Main Content */}
      <main className="modern-legal-main">
        <div className="modern-legal-container">
          <div className="modern-legal-document">
            <div className="modern-legal-meta">
              <div className="modern-legal-meta-content">
                <span className="modern-legal-last-updated">
                  Last Updated: {CONFIG.LEGAL_LAST_UPDATED_DATE}
                </span>
              </div>
              {/* Table of Contents */}
              <div className="modern-legal-table-of-contents">
                <h3 className="modern-legal-table-of-contents-title">Table of Contents</h3>
                {/* <ol className="modern-legal-table-of-contents-list">
                  <li><Link to="#overview">JuicyHouse.com Content Creator Agreement</Link></li>
                  <li><Link to="#platform-enrollment">Platform Enrollment</Link></li>
                  <li><Link to="#company-proprietary-rights">Company Proprietary Rights; Limited License</Link></li>
                  <li><Link to="#creator-account">Creator Account</Link></li>
                  <li><Link to="#creator-profile-offerings">Creator Profile and Offerings</Link></li>
                  <li><Link to="#relationship-parties">Relationship of the Parties</Link></li>
                  <li><Link to="#creator-compensation">Creator Compensation</Link></li>
                  <li><Link to="#statements-of-fact">Statements of Fact</Link></li>
                  <li><Link to="#privacy">Privacy</Link></li>
                  <li><Link to="#no-disparaging">No-Disparaging</Link></li>
                  <li><Link to="#termination">Termination</Link></li>
                  <li><Link to="#warranty-disclaimers">Warranty Disclaimers</Link></li>
                  <li><Link to="#limitation-liability">Limitation of Liability</Link></li>
                  <li><Link to="#waiver-california-civil-code">Waiver of California Civil Code Section 1542</Link></li>
                  <li><Link to="#scope-disclaimers-limitations">Scope of Disclaimers and Limitations</Link></li>
                  <li><Link to="#indemnification">Indemnification</Link></li>
                  <li><Link to="#dispute-resolution">Dispute Resolution</Link></li>
                  <li><Link to="#general">General</Link></li>
                </ol> */}

                <ol className="modern-legal-table-of-contents-list">
                  {TABLE_OF_CONTENTS.map((item, index) => (
                    <li key={item.id}>
                      <span className="cm_ol_count">{index + 1}.</span>
                      <a
                        href={`#${item.id}`}
                        onClick={(e) => scrollToSection(e, item.id)}
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
            <div className="modern-legal-content">
              <section id="overview" className="modern-legal-section">
                <h2 className="modern-legal-section-title">1. JuicyHouse.com Content Creator Agreement</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    <strong>
                      Last Updated: {CONFIG.LEGAL_LAST_UPDATED_DATE}
                    </strong>
                  </p>
                  <div className="modern-legal-warning-box">
                    <p className="modern-legal-text">
                      <strong>
                        This is a legally binding agreement between you and Prestige International, LLC, including any successor or affiliated company
                        or entity. This agreement contains disclaimers of
                        warranties, limitations on liability, releases, a
                        class-action waiver, and the requirement to{" "}
                        <span className="red-text">arbitrate</span> all claims
                        that may arise under this agreement.{" "}
                      </strong>
                    </p>
                    <p className="modern-legal-text">
                      <strong>
                        This agreement provides Juicy House with your absolute and
                        unconditional consent, waiver, and release of liability,
                        allowing Juicy House to publicize and commercially exploit
                        your name, likeness, and other personal characteristics.
                        These provisions form an essential basis of this
                        agreement.{" "}
                      </strong>
                    </p>
                    <p className="modern-legal-text">
                      <strong>
                        By clicking on the words &ldquo;I agree,&rdquo;
                        &ldquo;submit,&rdquo; or similar syntax or by otherwise
                        typing your name, you are electronically signing this
                        agreement, and thus agree to be bound by and acknowledge
                        your complete acceptance of all the express and
                        incorporated terms of this agreement. If you do not agree
                        to this agreement, you must not use the Juicy House
                        platform and must not register.
                      </strong>
                    </p>
                  </div>
                  <div className="modern-legal-prohibition-box">
                    <p className="modern-legal-text">
                      <strong className="red-text">
                        No Prostitution or Sex Trafficking.{" "}
                      </strong>
                      The promotion of prostitution, escort services, personal
                      companions, etc. are contrary to Juicy House&rsquo;s purpose
                      and will not be tolerated in any fashion. Juicy House
                      strictly prohibits you from using the Platform in any way to
                      engage in, participate in, assist, support, or facilitate
                      any act of prostitution or sex trafficking. You must not use
                      the Platform to exchange any personal information with any
                      user or have any communication with any user that would in
                      any way result in any type of face-to-face meeting involving
                      you and any user. If you violate this prohibition, Juicy
                      House will cancel your account, and you will forfeit any
                      payments owed to you under this agreement. Juicy House will
                      report any evidence of prostitution or sex trafficking to
                      the appropriate law enforcement agency.
                    </p>
                  </div>
                  <div className="modern-legal-parties">
                    <p className="modern-legal-text">
                      This content creator agreement is between{" "}
                      <strong>Prestige International, LLC,</strong> a Delaware Limited Liability Company
                      (the &ldquo;<strong>Company</strong>
                      &rdquo;), and you, the individual or entity signing up to be
                      a content creator (the &ldquo;<strong>Creator</strong>
                      &rdquo;).
                    </p>
                    <p className="modern-legal-text">
                      The Company owns and operates the social media content
                      sharing platform known as{" "}
                      <Link to={CONFIG.PLATFORM_URL}>JuicyHouse.com</Link> (the
                      &ldquo;<strong>Platform</strong>
                      &rdquo;), which provides billing solutions, advertising
                      media, storage and hosting, and virtual web space through a
                      proprietary web interface for personal communication and
                      entertainment.
                    </p>
                    <p className="modern-legal-text">
                      The Creator wants to use the Platform to monetize the
                      Creator&rsquo;s content and interact with the
                      Creator&rsquo;s fans.
                    </p>
                    <p className="modern-legal-text">The parties therefore agree as follows:</p>
                  </div>
                </div>
              </section>

              <section id="platform-enrollment" className="modern-legal-section">
                <h2 className="modern-legal-section-title">2. Platform Enrollment</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    2.1&nbsp; <strong>Eligibility. </strong>To participate in the Platform
                    as a content creator, the Creator must be at least 18-years
                    old (or the age of majority if higher) and have the legal
                    capacity to enter into legally binding contracts.
                  </p>
                  <p className="modern-legal-text">
                    2.2&nbsp; <strong>Registration. </strong>To register, the Creator must
                    (a) complete the applicable registration form; and (b)
                    submit a copy of a valid government-issued picture
                    identification (in color) that contains the Creator&rsquo;s
                    full legal name, date of birth, and expiration date for age
                    and identity verification purposes. If the Creator is in the
                    United States, the Creator must also submit a completed{" "}
                    <Link to={CONFIG.W9_FORM_URL}>W-9 Form</Link>.
                    By registering, the Creator states that all account
                    registration information is accurate. The Creator authorizes
                    the Company to verify the validity of the Creator&rsquo;s
                    government-issued identification with a third-party
                    verification service. The Company will notify the Creator of
                    acceptance or rejection by email to the email address that
                    the Creator supplied.
                  </p>
                </div>
              </section>

              <section id="company-proprietary-rights" className="modern-legal-section">
                <h2 className="modern-legal-section-title">3. Company Proprietary Rights; Limited License</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    3.1&nbsp; <strong>Ownership. </strong>The data and materials on the
                    Platform, except the Creator Content (as defined below),
                    including the text, graphics, interactive features, logos,
                    photos, music, videos, software, and all other audible,
                    visual, or downloadable materials, as well as the selection,
                    organization, coordination, compilation, and overall look
                    and feel of the Platform (collectively, the &ldquo;
                    <strong>Materials</strong>&rdquo;), are the intellectual
                    property of the Company, its licensors, and its suppliers.
                    The Materials are protected by United States copyright,
                    trade dress, patent, trademark, and other laws. Proprietary
                    rights and all rights to the Materials remain with the
                    Company, its licensors, or its suppliers, as the case may
                    be. The Creator does not acquire any ownership rights in the
                    Platform. Except as the Company expressly authorizes in
                    writing, the Creator will not reproduce, distribute, modify,
                    create derivative works of, publicly display, publicly
                    perform, republish, download, store, or transmit any of the
                    Materials. If, with authorization, the Creator downloads or
                    prints a copy of the Materials for use, the Creator must
                    retain all copyright, trademark, or other proprietary
                    notices. The Company reserves all rights not expressly
                    granted in and to the Platform and the Materials.
                  </p>
                  <p className="modern-legal-text">
                    3.2&nbsp;  <strong>Trademarks. </strong>The Company&rsquo;s name and
                    logo; the term JUICY HOUSE; and all related names, logos,
                    product and service names, designs, and slogans are the
                    Company&rsquo;s service marks and trademarks. The Creator
                    will not use these marks without the Company&rsquo;s written
                    permission. All other names, logos, product and service
                    names, designs, and slogans on the Platform are the service
                    marks and trademarks of their respective owners.
                  </p>
                  <p className="modern-legal-text">
                    3.3&nbsp;  <strong>Limited License. </strong>The Company hereby grants
                    the Creator a nonexclusive, nontransferable,
                    nonsublicensable (except as otherwise provided in this
                    agreement), revocable, limited license to access and use the
                    Platform and the related software solely under this
                    agreement (including the right to create a profile, upload
                    content, and sell content and other offerings through the
                    Platform). The Company prohibits any use other than
                    permitted by this agreement.
                  </p>
                  <p className="modern-legal-text">
                    3.4&nbsp;  <strong>Availability. </strong>The Company may alter,
                    remove, or discontinue any part of the Platform or the
                    Materials on the Platform, at any time, for any reason,
                    without notice, and the Company will not be liable to the
                    Creator in any way for possible consequences of those
                    changes.
                  </p>
                </div>
              </section>

              <section id="creator-account" className="modern-legal-section">
                <h2 className="modern-legal-section-title">4. Creator Account</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    4.1&nbsp; <strong>Account Creation.</strong> During the registration
                    process, the Creator will create an account by providing the
                    Company with accurate information as prompted by the
                    registration form, including a valid email address. The
                    Creator also will choose a password and a unique username.
                    The Creator must not select a username that is offensive or
                    that infringes another person&rsquo;s service mark,
                    trademark, or trade name.
                  </p>
                  <p className="modern-legal-text">
                    4.2&nbsp; <strong>Responsibility for Account.</strong> The Creator is
                    responsible for maintaining the confidentiality of the
                    Creator&rsquo;s password and account (including any
                    subaccounts). Further, the Creator is responsible for all
                    activities that occur under the Creator&rsquo;s account,
                    including any acts or omissions of any authorized persons
                    that the Creator adds to the Creator&rsquo;s account or any
                    subaccount. The Creator will promptly notify the Company of
                    any unauthorized use of the Creator&rsquo;s account
                    (including any subaccount) or any other security breach.
                  </p>
                  <p className="modern-legal-text">
                    4.3&nbsp;  <strong>Liability for Account Misuse. </strong>
                    The Company will not be liable for any loss that the Creator
                    may incur as a result of someone else using the
                    Creator&rsquo;s password or account, either with or without
                    the Creator&rsquo;s knowledge. The Creator could be held
                    liable for losses incurred by the Company or another person
                    due to someone else using the Creator&rsquo;s account or
                    password.
                  </p>
                  <p className="modern-legal-text">
                    4.4&nbsp;  <strong>Use of Other Accounts. </strong>The Creator will not
                    use anyone else&rsquo;s account at any time unless the
                    Creator is authorized in writing to do so.
                  </p>
                  <p className="modern-legal-text">
                    4.5&nbsp;  <strong>Account Security. </strong>The Company cares about
                    the integrity and security of the Creator&rsquo;s personal
                    information. But the Company cannot guarantee that
                    unauthorized persons will never be able to defeat the
                    Platform&rsquo;s security measures or use any personal data
                    the Creator provides to the Company for improper purposes.
                    The Creator acknowledges that the Creator provides personal
                    data at the Creator&rsquo;s own risk.
                  </p>
                </div>
              </section>

              <section id="creator-profile-offerings" className="modern-legal-section">
                <h2 className="modern-legal-section-title">5. Creator Profile and Offerings</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    5.1&nbsp;
                    The Creator may create one or more profiles (the &ldquo;
                    <strong>Creator Profile</strong>&rdquo;) on the Platform to
                    offer various content and services (collectively, the
                    &ldquo;
                    <strong>Creator Offerings</strong>&rdquo;). The Creator may
                    provide free content and allow end-users to subscribe to the
                    Creator Profile for a one-time or recurring fee.
                  </p>
                  <p className="modern-legal-text">
                    5.2&nbsp;
                    The Creator may upload to or display through the Creator
                    Profile various media, content, and material, including
                    videos, photographs, audio, text, audiovisual, graphics,
                    music, trademarks, logos, and artwork (collectively, the
                    &ldquo;
                    <strong>Creator Content</strong>&rdquo;). The Creator states
                    that the Creator owns or has a license to publish, publicly
                    display, publicly perform, distribute, and permit the use of
                    the Creator Content by the Company and the Platform users as
                    contemplated by this agreement and the{" "}
                    <Link to={ROUTES.LEGAL_TERMS}>
                      Terms-of-Service Agreement
                    </Link>
                    .
                  </p>
                  <p className="modern-legal-text">
                    5.3&nbsp;
                    The Creator may use various interactive features, including
                    messaging, email, and comments sections designed to foster
                    interactions between the Creator and end-users (the &ldquo;
                    <strong>Interactive Services</strong>
                    &rdquo;). The Creator will remain responsible for all
                    feedback transmitted by the Creator or end-users through the
                    Interactive Services.
                  </p>
                  <p className="modern-legal-text">
                    5.4&nbsp;
                    The Creator acknowledges that the Creator is solely
                    responsible for the Creator Content that the Creator offers,
                    publishes, transmits, or posts on or through the Creator
                    Profile or the Interactive Services. The Creator Profile,
                    the Creator Content, and the Interactive Services must not:
                  </p>
                  <p className="modern-legal-text">
                    (a)&nbsp;
                    Contain any material that is harmful, threatening,
                    defamatory, libelous, slanderous, obscene, indecent,
                    abusive, offensive, harassing, violent, hateful,
                    inflammatory, or otherwise objectionable;
                  </p>
                  <p className="modern-legal-text">
                    (b)&nbsp;
                    Depict or portray minors, incest, rape or nonconsensual sex,
                    extreme violence, torture, nonconsensual pain, blood,
                    cutting, erotic asphyxiation, torture, necrophilia,
                    sadomasochistic abuse or bondage, genital mutilation,
                    bestiality, urination, defecation, enema play, vomiting,
                    menstrual bleeding, or paraphilia;
                  </p>
                  <p className="modern-legal-text">
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>
                    &nbsp;
                    Promote violence or discrimination based on race, sex,
                    religion, nationality, disability, sexual orientation, or
                    age;
                  </p>
                  <p className="modern-legal-text">
                    (d)&nbsp;
                    Infringe any copyright, patent, service mark, trademark,
                    trade name, trade secret, or other intellectual property or
                    proprietary rights of any other person;
                  </p>
                  <p className="modern-legal-text">
                    (e)&nbsp;
                    Violate the legal rights (including the rights of publicity
                    and privacy) of others or contain any material that could
                    give rise to any civil or criminal liability under
                    applicable law or otherwise may be in conflict with this
                    agreement or the{" "}
                    <Link to={ROUTES.LEGAL_TERMS}>
                      Terms-of-Service Agreement
                    </Link>
                    ;
                  </p>
                  <p className="modern-legal-text">
                    (f)&nbsp;
                    Be likely to deceive any person;
                  </p>
                  <p className="modern-legal-text">
                    (g)&nbsp;
                    Discuss, facilitate, promote, advertise, or solicit any
                    illegal activity, or advocate, discuss, facilitate, promote,
                    advertise, solicit, or assist any unlawful act, including
                    prostitution or sex trafficking;
                  </p>
                  <p className="modern-legal-text">
                    (h)&nbsp;
                    Cause annoyance, inconvenience, or needless anxiety or be
                    likely to upset, embarrass, alarm, or annoy any other
                    person;
                  </p>
                  <p className="modern-legal-text">
                    (i)&nbsp;
                    Impersonate any person, or misrepresent the Creator&rsquo;s
                    identity or affiliation with any individual or organization;
                  </p>
                  <p className="modern-legal-text">
                    (j)&nbsp;
                    Display any telephone numbers, street addresses, last names,
                    email addresses, URLs, or any confidential information of
                    any person;
                  </p>
                  <p className="modern-legal-text">
                    (k)&nbsp;
                    Display any images or videos containing confidential or
                    personal information without authorization;
                  </p>
                  <p className="modern-legal-text">
                    (l)&nbsp;
                    Give the impression that it emanates from or is endorsed by
                    the Company or any other individual or entity, if this is
                    not the case; or
                  </p>
                  <p className="modern-legal-text">
                    (m)&nbsp;
                    Contain technically harmful material, including computer
                    viruses, logic bombs, Trojan horses, worms, malware,
                    ransomware, harmful components, corrupted data, or other
                    malicious software or harmful data.
                  </p>
                  <p className="modern-legal-text">
                    Engaging in any of the above practices will be considered a
                    material breach of this agreement and may result in
                    immediate termination of the Creator&rsquo;s account and
                    access to the Platform without notice, in the
                    Company&rsquo;s sole discretion. The Company may pursue any
                    legal remedies against the Creator if the Creator engages in
                    any of the above-prohibited conduct. The Company will also
                    report any Creator Offering that could be considered
                    exploitative of children in any way to the National Center
                    for Missing and Exploited Children&rsquo;s CyberTipline and
                    any other applicable legal and regulatory bodies.
                  </p>
                  <p className="modern-legal-text">
                    5.5&nbsp;
                    The Company may reject or remove any Creator Offering,
                    including Creator Content, at any time and for any reason.
                    The reasons for rejection may include where the Company
                    believes that the Creator Offering breaches this agreement,
                    including section 5.4.
                  </p>
                  <p className="modern-legal-text">
                    5.6&nbsp;
                    The Creators end-users must submit payment through the Platforms billing interface.
                    The Creator is solely responsible for setting the prices for the Creator Offerings,
                    including the subscription fees for the Creator Profile.
                    The Company permits Creators to retain 100% of the amounts they charge.
                  </p>
                  <p className="modern-legal-text">
                    5.7&nbsp;
                    The Company is not required to review, endorse, police, or
                    enforce any relationships, interactions, or content shared
                    between the Creator and any end-user. Nor is the Company
                    required to resolve any dispute between the Creator and any
                    end-user or any other person.
                  </p>
                  <p className="modern-legal-text">
                    5.8&nbsp;
                    The Creator will comply with the federal record-keeping and
                    labeling requirements codified at 18 U.S.C.
                    &sect;&sect;&nbsp; 2257&ndash;2257A and 28 C.F.R. Part 75.
                    The Creator will obtain and keep all records necessary to
                    demonstrate that the Creator complies with 18 U.S.C.
                    &sect;&sect;&nbsp; 2257&ndash;2257A and 28 C.F.R. Part 75,
                    including legible copies of picture identification cards (as
                    defined by 28 C.F.R. 75.1) for each individual appearing in
                    any Creator Content on the date of the production of that
                    Creator Content. The Creator or a third-party service
                    provider will act as the &ldquo;Custodian of Records&rdquo;
                    as required by 28 C.F.R. Part 75 and will keep all required
                    records at the Creator&rsquo;s or that third party&rsquo;s
                    primary address. The Creator will make available to the
                    Company or any government official, and copy at the
                    Company&rsquo;s request, all records required to be kept
                    under 18 U.S.C. &sect;&sect;&nbsp; 2257&ndash;2257A and 28
                    C.F.R. Part 75.
                  </p>
                  <p className="modern-legal-text">
                    5.9&nbsp;
                    <strong>Creator Proprietary Rights</strong>
                  </p>
                  <p className="modern-legal-text">
                    (a)&nbsp;
                    <strong>Creator Content Ownership. </strong>
                    The Company does not claim any ownership rights in the
                    Creator Profile or the Creator Content. The Creator
                    continues to retain any ownership rights that the Creator
                    may have in the Creator Profile and the Creator Content
                    subject to the licenses granted in section 5.9(b) and 5.9
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>
                    .
                  </p>
                  <p className="modern-legal-text">
                    (b)&nbsp;
                    <strong>Creator License Grant.</strong> The Creator hereby
                    grants the Company, its affiliates, and service providers,
                    and each of their and the Company&rsquo;s respective
                    licensees a perpetual, worldwide, irrevocable,
                    sublicensable, transferrable license to host, cache, route,
                    transmit, store, copy, modify, distribute, publicly perform
                    and display (through all media now known or later created),
                    reformat, excerpt, analyze, create algorithms based on,
                    prepare derivative works of, sell, exploit, and otherwise
                    use the Creator Profile and the Creator Content solely in
                    connection with the Platform, and the Company&rsquo;s
                    business, including for (a) displaying the Creator Profile
                    and the Creator Content on the Platform; (b) allowing other
                    users to view the Creator Profile and the Creator Content;
                    and{" "}
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>{" "}
                    promoting the Creator Profile, the Creator Content, and the
                    Platform. The foregoing license includes the right to
                    reproduce, distribute, display, perform, make derivative
                    works from, or otherwise exploit the Creator Profile and the
                    Creator Content in proximity with or in connection with any
                    third-party content, including advertisements.
                  </p>
                  <p className="modern-legal-text">
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>
                    &nbsp;
                    <strong>
                      Use of Creator&rsquo;s Fictitious Name, Likeness, and
                      Information.
                    </strong>{" "}
                    The Creator hereby grants the Company and its affiliates,
                    and each of their respective direct and indirect successors,
                    licensees, and assigns the right to use the Creator&rsquo;s
                    fictitious name, image, likeness, and biographical and
                    professional information, including information the Creator
                    provides to the Company and any other information publicly
                    available about the Creator, in connection with the Platform
                    and the Creator Offerings, including advertising and
                    promoting the Platform and the Creator Offerings, in
                    perpetuity throughout the universe and in any medium or
                    format now existing or later developed without further
                    consent from or any royalty, payment, or other compensation
                    to the Creator. The Creator acknowledges that the
                    Creator&rsquo;s fictitious name and likeness may appear on
                    websites that contain pornographic content, including
                    content that the Creator might consider obscene, indecent,
                    offensive, or otherwise objectionable. The Creator waives
                    any right to inspect or approve the Company&rsquo;s use of
                    the Creator&rsquo;s fictitious name and likeness.{" "}
                    <strong>
                      The Creator must obtain a signed written appearance
                      release, waiver of rights, and release of claims for each
                      identifiable person in the Creator Content to allow the
                      use of their fictitious name and likeness in the Creator
                      Content and to allow the Creator Content to be posted on
                      the Platform
                    </strong>
                    .
                  </p>
                  <p className="modern-legal-text">
                    (d)&nbsp;
                    <strong>Moral Rights Waiver. </strong>The Creator waives all
                    claims the Creator may now or later have in any jurisdiction
                    to so-called &ldquo;moral rights&rdquo; or rights of droit
                    moral in the Creator Profile and the Creator Content.
                  </p>
                </div>
              </section>

              <section id="relationship-parties" className="modern-legal-section">
                <h2 className="modern-legal-section-title">6. Relationship of the Parties</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    6.1&nbsp;
                    The parties intend that their relationship will be that of
                    independent contractors and not business partners. This
                    agreement does not, and the parties do not intend it to,
                    create a partnership, joint venture, agency, franchise, or
                    employment relationship between the parties and the parties
                    expressly disclaim the existence of any of these
                    relationships between them. Neither party is the agent for
                    the other, and neither party has the right to bind the other
                    on any agreement with another person.
                  </p>
                  <p className="modern-legal-text">
                    6.2&nbsp;
                    The Creator is not eligible under this agreement to
                    participate in any vacation, group medical or life
                    insurance, disability, profit sharing or retirement
                    benefits, or any other fringe benefits or benefit plans
                    offered by the Company to its employees.
                  </p>
                  <p className="modern-legal-text">
                    6.3&nbsp;
                    The Company is not responsible for withholding or paying any
                    income, payroll, Social Security, or other federal, state,
                    or local taxes; making any insurance contributions,
                    including unemployment or disability; or obtaining
                    worker&rsquo;s compensation insurance on the Creator&rsquo;s
                    behalf. The Creator is solely responsible for all those
                    taxes or contributions, including penalties and interest.
                    The Creator is also responsible for maintaining adequate
                    worker&rsquo;s compensation coverage or insurance for the
                    Creator and any employee or other personnel engaged by the
                    Creator.
                  </p>
                  <p className="modern-legal-text">
                    6.4&nbsp;
                    Any persons employed or engaged by the Creator in connection
                    with the creation, production, or submission of the Creator
                    Content must be the Creator&rsquo;s employees or
                    contractors, and the Creator will be fully responsible for
                    them and indemnify the Company against any claims made by or
                    on behalf of any such employee or contractor. The Creator
                    will require each such employee and contractor to sign
                    written agreements securing for the Company all rights
                    granted to the Company in this agreement before that
                    employee or contractor provides or creates any Creator
                    Content in connection with this agreement.
                  </p>
                </div>
              </section>

              <section id="creator-compensation" className="modern-legal-section">
                <h2 className="modern-legal-section-title">7. Creator Compensation</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    7.1&nbsp;
                    <strong>Creator Revenue.</strong> The Creator will receive
                    100% of the Net Revenue from the sale of the Creator
                    Offerings during this agreement (the &ldquo;
                    <strong>Creator Revenue</strong>&rdquo;).
                    <strong>Net Revenue</strong>&rdquo; means gross revenue
                    received from end-users for the Creator Offerings during
                    this agreement, less the Service Fee, Transactional Costs,
                    chargebacks, and refunds and excluding any taxes (including
                    VAT). &ldquo;
                    <strong>Transactional Costs</strong>&rdquo; are amounts paid
                    by the Company to third parties to facilitate a transaction
                    and may include payment-processing fees, foreign exchange
                    fees, and platform fees (e.g., for transactions occurring
                    through third-party stores or systems).
                  </p>
                  <p className="modern-legal-text">
                    7.2&nbsp;<strong>Payouts. </strong>
                    The Company will deposit the Creator Revenue in the Creator's online wallet. The Company will direct deposit the amount in the Creator's online wallet to the Creator's bank account within 7 days of the initiated payout on condition that the Creator's online wallet has a minimum of $30. The Company will deduct a $15 bank fee from the amount in the Creators online wallet for each direct deposit over $100. The Creator may request the Company to deposit the Creator Revenue to the Creator's bank account before the weekly deposit if the Creator's online wallet has a minimum of $250. The Company will deduct a 3% wire transfer fee along with any additional fees incurred in processing the transfer from the balance in the Creator's online wallet for all non-standard payouts. All payouts are in U.S. dollars.
                  </p>
                  <p className="modern-legal-text">
                    7.3&nbsp;
                    <strong>Taxes. </strong>Income taxes are solely the
                    Creator&rsquo;s responsibility. The Company may withhold
                    from any amounts due to the Creator that the Company
                    determines in good faith must be withheld under U.S. tax
                    law. The Company may file reports concerning income with any
                    taxing authority, including the U.S. Internal Revenue
                    Service.
                  </p>
                  <p className="modern-legal-text">
                    7.4&nbsp;
                    <strong>Limitations. </strong>The Company is not required to
                    payout Creator Revenue for any transaction that is subject
                    to a refund or chargeback. The Company may withhold amounts
                    or make adjustments in its sole discretion (a) to satisfy
                    any refunds that it may make (including for technical
                    errors); (b) pending any investigation of fraud or other
                    illegality;{" "}
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>{" "}
                    due to excessive chargebacks or refunds; (d) due to breach
                    of this agreement; and (e) as required by U.S. tax law. If
                    the Company is unable to payout the Creator after six months
                    as a result of inaccurate information in the Creator&rsquo;s
                    account and the Company is unable to contact the Creator,
                    that Creator Revenue will be forfeited and become the
                    Company&rsquo;s property.
                  </p>
                  <p className="modern-legal-text">
                    7.5&nbsp;
                    <strong>Disputes. </strong>If the Creator disputes the
                    calculation of the Creator Revenue, the Creator must notify
                    the Company in writing no later than 30 days after the
                    disputed calculation. Failure to notify the Company within
                    this period will result in the Creator&rsquo;s waiver of any
                    claims related to the disputed calculation.
                  </p>
                </div>
              </section>

              <section id="statements-of-fact" className="modern-legal-section">
                <h2 className="modern-legal-section-title">8. Statements of Fact</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    The Creator states that the following facts are accurate and will continue to be
                    accurate during this agreement:
                  </p>
                  <p className="modern-legal-text">
                    8.1&nbsp;
                    The Creator is at least 18-years old and has the legal
                    capacity to enter into this agreement.
                  </p>
                  <p className="modern-legal-text">
                    8.2&nbsp;
                    The Creator has the power to enter into this agreement, to
                    grant the rights and licenses granted in it, and to perform
                    the Creator&rsquo;s obligations in this agreement.
                  </p>
                  <p className="modern-legal-text">
                    8.3&nbsp;
                    The Creator is duly licensed, authorized, and certified by
                    all applicable governmental and regulatory authorities to
                    perform the Creator&rsquo;s duties and obligations under
                    this agreement.
                  </p>
                  <p className="modern-legal-text">
                    8.4&nbsp;
                    The Creator has independently evaluated the desirability of
                    participating in the Platform, and the Creator has not
                    relied on any statement other than those in this agreement.
                  </p>
                  <p className="modern-legal-text">
                    8.5&nbsp;
                    The Creator&rsquo;s signing and performance of this
                    agreement will not conflict with or violate (i) any order,
                    judgment, or decree that applies to the Creator; or (ii) any
                    agreement that applies to the Creator.
                  </p>
                  <p className="modern-legal-text">
                    8.6&nbsp;
                    The Creator&rsquo;s performance under this agreement will
                    not:
                  </p>
                  <p className="modern-legal-text">
                    (a)&nbsp;
                    invade the right of privacy or publicity of any person
                    (including invasion of rights of celebrity);
                  </p>
                  <p className="modern-legal-text">
                    (b)&nbsp;
                    involve any defamatory, libelous, slanderous, obscene,
                    indecent, or otherwise unlawful material;
                  </p>
                  <p className="modern-legal-text">
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>
                    &nbsp;
                    violate any applicable law, including 18 U.S.C.
                    &sect;&nbsp; 1591 (sex trafficking of children or by force,
                    fraud, or coercion), and 18 U.S.C. &sect;&nbsp; 2421A
                    (promotion or facilitation of prostitution and reckless
                    disregard of sex trafficking); or
                  </p>
                  <p className="modern-legal-text">
                    (d)&nbsp;
                    otherwise infringe on the rights of any third parties,
                    including those of copyright, patent, trademark, service
                    mark, trade name, trade secret, or other
                    intellectual-property rights, or engage in false
                    advertising, unfair competition, violation of
                    antidiscrimination law, or violation of any other right of
                    any person.
                  </p>
                  <p className="modern-legal-text">
                    8.7&nbsp;
                    The Creator owns all interest in the Creator Content that is
                    uploaded to the Creator Profile or otherwise used in
                    connection with the Platform.
                  </p>
                  <p className="modern-legal-text">
                    8.8&nbsp;
                    The Creator has sufficient rights in the Creator Content to
                    grant the Company the rights granted in this agreement
                    (including a signed written appearance release for each
                    identifiable person in the Creator Content to use their
                    fictitious name and likeness).
                  </p>
                  <p className="modern-legal-text">
                    8.9&nbsp;
                    The Creator Content does not depict any individual under
                    18-years old.
                  </p>
                  <p className="modern-legal-text">
                    8.10&nbsp;
                    The Creator complies with 18 U.S.C.
                    &sect;&sect;&nbsp; 2257&ndash;2257A and 28 C.F.R. Part 75,
                    including inspecting and keeping all required written
                    documents, including written documents sufficient to confirm
                    that all subjects of the Creator Content were at least
                    18-years old at the time of the production of the Creator
                    Content as required by 18 U.S.C.
                    &sect;&sect;&nbsp; 2257&ndash;2257A and 28 C.F.R. Part 75,
                    and will provide the Company with copies of all required
                    written documents on request.
                  </p>
                </div>
              </section>

              <section id="privacy" className="modern-legal-section">
                <h2 className="modern-legal-section-title">9. Privacy</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    For information about how the Company collects, uses, and shares the Creator&rsquo;s
                    personal information, please review the{" "}
                    <Link to={ROUTES.LEGAL_PRIVACY}>Privacy Policy</Link>. The
                    Creator acknowledges that the Company may retain
                    indefinitely information the Creator submits to the Company,
                    including a copy of the Creator&rsquo;s government-issued
                    identification, in case the information is needed to comply
                    with applicable law, including 18 U.S.C.
                    &sect;&sect;&nbsp; 2257&ndash;2257A and 28 C.F.R. Part 75, or
                    in a good-faith belief that preservation or disclosure of
                    that information is reasonably necessary, in the
                    Company&rsquo;s opinion, to (a) comply with legal process,
                    including civil and criminal subpoenas, court orders, or
                    other compulsory disclosure; (b) enforce this agreement;{" "}
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>{" "}
                    respond to claims of a violation of the rights of third
                    parties, regardless of whether the third party is a user,
                    individual, or government agency; or (d) protect the rights,
                    property, or personal safety of the Company, the
                    Platform&rsquo;s users, or the public.
                  </p>
                </div>
              </section>

              <section id="no-disparaging" className="modern-legal-section">
                <h2 className="modern-legal-section-title">10. No-Disparaging</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    During this agreement and for two years after its termination, the Creator must not
                    say to anyone, in writing or orally, anything critical of
                    the Company, its business, or any of its then-current
                    members, officers, employees, and customers. This section 9
                    does not prevent the Creator from exercising rights that, by
                    law, the Creator cannot waive by contract or from disclosing
                    information to the extent required to comply with the law.
                  </p>
                </div>
              </section>

              <section id="termination" className="modern-legal-section">
                <h2 className="modern-legal-section-title">11. Termination</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    11.1&nbsp;
                    <strong>Termination on Notice. </strong>
                    Either party may terminate this agreement at any time for
                    any reason by written notice to the other party.
                  </p>
                  <p className="modern-legal-text">
                    11.2&nbsp;
                    <strong>Termination by Company. </strong>The Company may
                    suspend, disable, or terminate the Creator&rsquo;s account
                    if the Company determines, in its sole discretion, that the
                    Creator (a) breached this agreement; (b) engaged in
                    fraudulent, illegal, or suspicious activity;{" "}
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>{" "}
                    engaged in conduct that would tend to damage the
                    Company&rsquo;s reputation and goodwill; or (d) for any
                    other reason.
                  </p>
                  <p className="modern-legal-text">
                    11.3&nbsp;
                    <strong>Effect of Termination. </strong>On termination, the
                    Creator&rsquo;s right to access the Platform and all
                    licenses granted by the Company terminate, and the Company
                    will remit to the Creator, no later than 45 days after the
                    date of termination, all outstanding and undisputed Creator
                    Revenues earned under this agreement. In addition, the
                    Creator acknowledges that the Company may set off against
                    any Creator Revenues owed to the Creator any loss incurred
                    under section 16 (Indemnification).
                  </p>
                  <p className="modern-legal-text">
                    11.4&nbsp;
                    <strong>Survival. </strong>Any part of this agreement that
                    imposes an obligation after termination will survive the
                    termination, including all warranty disclaimers and
                    limitations of liability.
                  </p>
                </div>
              </section>

              <section id="warranty-disclaimers" className="modern-legal-section">
                <h2 className="modern-legal-section-title">12. Warranty Disclaimers</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    12.1&nbsp;
                    The Company is not making any guarantee of profitability or
                    about the amount of money the Creator will earn under this
                    agreement. The Creator acknowledges that past earnings do
                    not guarantee or suggest similar future earnings.
                  </p>
                  <p className="modern-legal-text">
                    12.2&nbsp;
                    The Creator assumes sole responsibility for all risks,
                    consequences, and damages resulting from the Creator&rsquo;s
                    interaction and association with the Platform, including
                    risks associated with the publicity of appearing on the
                    Internet; the risk of recording, piracy, or unauthorized
                    dissemination of the Creator Content; or the risk of
                    publication of the Creator&rsquo;s identity, including
                    publication of the Creator&rsquo;s personal information.
                  </p>
                  <p className="modern-legal-text">
                    12.3&nbsp;
                    The Company is not making any warranty&mdash;express or
                    implied&mdash;that
                  </p>
                  <p className="modern-legal-text">
                    (a)&nbsp;
                    the use of the Platform will be timely, uninterrupted, or
                    error-free (whether as a result of technical failure, acts
                    or omissions of third parties, or other causes) or will
                    operate in combination with any other hardware, software,
                    system, or data;
                  </p>
                  <p className="modern-legal-text">
                    (b)&nbsp;
                    the Platform will meet the Creator&rsquo;s requirements or
                    expectations;
                  </p>
                  <p className="modern-legal-text">
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>
                    &nbsp;
                    the Platform will be accurate or reliable;
                  </p>
                  <p className="modern-legal-text">
                    (d)&nbsp;
                    errors or defects in the Platform will be corrected; or
                  </p>
                  <p className="modern-legal-text">
                    (e)&nbsp;
                    the servers that make the Platform available are free of
                    viruses or other harmful components.
                  </p>
                  <p className="modern-legal-text">
                    12.4&nbsp;
                    The Company offers the Platform &ldquo;as is.&rdquo; The
                    Company is not making any warranty, either express or
                    implied, including any implied warranty of merchantability,
                    fitness for a particular purpose, and noninfringement for
                    the Platform. No advice or information, whether oral or
                    written, obtained from the Company, the Platform, or
                    elsewhere will create any warranty not expressly stated
                    here.
                  </p>
                </div>
              </section>

              <section id="limitation-liability" className="modern-legal-section">
                <h2 className="modern-legal-section-title">13. Limitation of Liability</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    13.1&nbsp;
                    The Company will not be liable for any of the following:
                  </p>
                  <p className="modern-legal-text">
                    (a)&nbsp;
                    Errors, mistakes, or inaccuracies of the Platform;
                  </p>
                  <p className="modern-legal-text">
                    (b)&nbsp;
                    Personal injury or property damage resulting from the
                    Creator&rsquo;s access to or use of the Platform;
                  </p>
                  <p className="modern-legal-text">
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>
                    &nbsp;
                    Content (including Creator Content) or conduct that is
                    infringing, inaccurate, obscene, indecent, offensive,
                    threatening, harassing, defamatory, libelous, abusive,
                    invasive of privacy, or illegal;
                  </p>
                  <p className="modern-legal-text">
                    (d)&nbsp;
                    Unauthorized access to or use of the Company&rsquo;s servers
                    and any personal or financial information stored in them,
                    including unauthorized access or changes to the
                    Creator&rsquo;s account, the Creator Content, transmissions,
                    or data;
                  </p>
                  <p className="modern-legal-text">
                    (e)&nbsp;
                    Interruption or cessation of transmission to or from the
                    Platform;
                  </p>
                  <p>
                    (f)&nbsp;
                    Denial-of-service attack or distributed denial-of-service
                    attack;
                  </p>
                  <p className="modern-legal-text">
                    (g)&nbsp;
                    Bugs, viruses, Trojan horses, malware, ransomware, or other
                    disabling code that may be transmitted to or through the
                    Platform by any person or that might infect the
                    Creator&rsquo;s computer or affect the Creator&rsquo;s
                    access to or use of the Platform or the Creator&rsquo;s
                    other services, hardware, or software;
                  </p>
                  <p className="modern-legal-text">
                    (h)&nbsp;
                    Incompatibility between the Platform and the Creator&rsquo;s
                    other services, hardware, or software;
                  </p>
                  <p className="modern-legal-text">
                    (i)&nbsp;
                    Delays or failures the Creator might experience in starting,
                    conducting, or completing any transmissions to or
                    transactions through or with the Platform; or
                  </p>
                  <p className="modern-legal-text">
                    (j)&nbsp;
                    Loss or damage incurred because of the use of any content
                    posted, emailed, sent, or otherwise made available through
                    the Platform.
                  </p>
                  <p className="modern-legal-text">
                    13.2&nbsp;
                    The Company will not be liable to the Creator for
                    breach-of-contract damages that the Company could not
                    reasonably have foreseen on entry into this agreement. The
                    Company also will not be liable to the
                    Creator&mdash;regardless of theory of liability and even if
                    the Company knew or should have known of the possibility of
                    these damages&mdash;for damages for any of the following:
                    (a) personal injury; (b) pain and suffering;{" "}
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>{" "}
                    emotional distress; (d) loss of use; (e) loss of services;
                    (f) loss of profits; (g) loss of revenue; (h) loss of
                    goodwill; (i) loss of contracts; (j) loss of data; (k) loss
                    of privacy; (l) loss of business or opportunity; or (m) cost
                    of obtaining substitute services related to the Platform.
                  </p>
                  <p className="modern-legal-text">
                    13.3&nbsp;
                    Except as stated in section 15, neither party will be liable
                    to the other party for indirect, incidental, special,
                    statutory, exemplary, or punitive damages arising from or
                    relating to this agreement, regardless of the theory of
                    liability and even if that party knew or should have known
                    of the possibility of these damages, including loss of
                    revenue or anticipated profits or lost business.
                  </p>
                  <p className="modern-legal-text">
                    13.4&nbsp;
                    The Company&rsquo;s total cumulative liability to the
                    Creator will not exceed the greater of (a) total amount owed
                    to the Creator under this agreement and (b) $500.
                  </p>
                </div>
              </section>

              <section id="waiver-california-civil-code" className="modern-legal-section">
                <h2 className="modern-legal-section-title">14. Waiver of California Civil Code Section 1542</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    If the Creator resides in California, the Creator
                    acknowledges that the Creator understands the consequences
                    of entering into the general release and discharge of all
                    known and unknown claims as stated in this agreement and
                    that the Creator is familiar with the provisions of section
                    1542 of the California Civil Code, which provides that:
                  </p>
                  <p className="modern-legal-text">
                    <strong>
                      A general release does not extend to claims that the
                      creditor or releasing party does not know or suspect to
                      exist in his or her favor at the time of executing the
                      release and that, if known by him or her, would have
                      materially affected his or her settlement with the debtor
                      or released party
                    </strong>
                    .
                  </p>
                  <p className="modern-legal-text">
                    The Creator waives all rights under section 1542 and any
                    other federal or state statutes or laws of similar effect.
                  </p>
                </div>
              </section>

              <section id="scope-disclaimers-limitations" className="modern-legal-section">
                <h2 className="modern-legal-section-title">15. Scope of Disclaimers and Limitations</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    The disclaimers and limits stated in sections 11 and 12
                    apply to the greatest extent allowed by law, but no more.
                    The Company does not intend to deprive the Creator of any
                    mandatory protections provided to the Creator by law.
                    Because some jurisdictions may prohibit the disclaimer of
                    some warranties, the limitation of some damages, or other
                    matters, one or more of the disclaimers or limitations might
                    not apply to the Creator.
                  </p>
                </div>
              </section>

              <section id="indemnification" className="modern-legal-section">
                <h2 className="modern-legal-section-title">16. Indemnification</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    16.1&nbsp;
                    <strong>In General. </strong>The Creator will pay the
                    Company for any loss of the Company&rsquo;s that is caused
                    by (a) the Creator&rsquo;s use of the Platform; (b) the
                    Creator Offerings;{" "}
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>{" "}
                    the Creator&rsquo;s dispute with any end-user or other
                    person; (d) the Creator&rsquo;s breach of this agreement;
                    (e) the Creator&rsquo;s infringement of third-party
                    intellectual property rights or violation of any other
                    third-party rights; (f) the Creator&rsquo;s violation of
                    applicable law; (h) the Creator&rsquo;s tortious acts or
                    omissions; or (i) the Creator&rsquo;s criminal acts or
                    omissions. But the Creator is not required to pay if the
                    loss was caused by the Company&rsquo;s intentional
                    misconduct.
                  </p>
                  <p className="modern-legal-text">
                    16.2&nbsp;
                    <strong>Definitions. </strong>&ldquo;
                    <strong>Loss</strong>
                    &rdquo; means an amount that the Company is legally
                    responsible for or pays in any form. Amounts include, for
                    example, a judgment, a settlement, a fine, damages,
                    injunctive relief, staff compensation, a decrease in
                    property value, and expenses for defending against a claim
                    for a loss (including fees for legal counsel, expert
                    witnesses, and other advisers). A loss can be tangible or
                    intangible; can arise from bodily injury, property damage,
                    or other causes; can be based on tort, breach of contract,
                    or any other theory of recovery; and includes incidental,
                    direct, and consequential damages. A loss is &ldquo;
                    <strong>caused by</strong>&rdquo; an event if the loss would
                    not have occurred without the event, even if the event is
                    not a proximate cause of the loss.
                  </p>
                  <p className="modern-legal-text">
                    16.3&nbsp;
                    <strong>Company&rsquo;s Duty to Notify.</strong> The Company
                    will notify the Creator before the 15th business day after
                    the Company knows or should reasonably have known of a claim
                    for a loss that the Creator might be obligated to pay. The
                    Company&rsquo;s failure to give the Creator timely notice
                    does not terminate the Creator&rsquo;s obligation, except to
                    the extent that the failure prejudices the Creator&rsquo;s
                    ability to defend the claim or mitigate losses.
                  </p>
                  <p className="modern-legal-text">
                    16.4&nbsp;
                    <strong>Legal Defense of a Claim. </strong>
                    The Company has control over defending a claim for a loss
                    (including settling it) unless the Company directs the
                    Creator to control the defense. The Company and the Creator
                    will cooperate in good faith on a claim.
                  </p>
                  <p className="modern-legal-text">
                    16.5&nbsp;
                    <strong>No Exclusivity. </strong>The Company&rsquo;s rights
                    under this section 16 do not affect other rights that the
                    Company might have.
                  </p>
                </div>
              </section>

              <section id="dispute-resolution" className="modern-legal-section">
                <h2 className="modern-legal-section-title">17. Dispute Resolution</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    17.1&nbsp;
                    <strong>Litigation Election. </strong>Either party may elect
                    to litigate the following type of case or controversy: (a)
                    an action seeking injunctive relief, or (b) a suit to compel
                    compliance with this section 16.
                  </p>
                  <p className="modern-legal-text">
                    17.2&nbsp;
                    <strong>Arbitration. </strong>The parties will settle any
                    dispute arising out of or relating to this agreement, or the
                    breach of it, by arbitration administered by the American
                    Arbitration Association under its Commercial Arbitration
                    Rules. The arbitrator, and not any federal, state, or local
                    court or agency, will have exclusive authority to resolve
                    all disputes arising out of or relating to the
                    interpretation, enforceability, or formation of this
                    agreement, including any claim that all or any part of this
                    agreement is void or voidable. A single arbitrator will
                    preside over the arbitration and issue a final award on all
                    issues submitted to the arbitrator. The arbitrator may grant
                    whatever relief that would be available in a court at law or
                    in equity, except that the arbitrator will not award
                    punitive or exemplary damages, or damages otherwise limited
                    or excluded in this agreement. The arbitrator&rsquo;s award
                    will be binding on the parties and may be entered as a
                    judgment in any court of competent jurisdiction. Arbitration
                    will take place in Wilmington, Delaware, unless the parties
                    agree otherwise in writing. The parties will bear equally
                    the costs of arbitration, including the fees and expenses of
                    the arbitrator, and each party will bear the costs
                    associated with its case, except the arbitrator will award
                    costs and fees to the Prevailing Party under section 16.6.
                    Unless required by law, neither a party nor an arbitrator
                    will disclose the existence, content, or results of any
                    arbitration under this agreement without the parties&rsquo;
                    prior written consent.
                  </p>
                  <p className="modern-legal-text">
                    17.3&nbsp;
                    <strong>Injunctive Relief. </strong>The Creator acknowledges
                    that breach by the Creator of this agreement could cause
                    irreparable harm for which damages would be an inadequate
                    remedy. If any breach occurs or is threatened, the Company
                    may seek an injunction, a restraining order, or any other
                    equitable remedy, in each case without posting a bond or
                    other security.
                  </p>
                  <p className="modern-legal-text">
                    17.4&nbsp;
                    <strong>Jurisdiction</strong>
                    <strong> and Venue. </strong>If a party brings any
                    proceeding seeking an injunction, a restraining order, or
                    any other equitable remedy to which that party is entitled
                    under this agreement, that party will bring that proceeding
                    only in the United States District Court for the District of
                    Delaware or in any state court of competent jurisdiction in
                    New Castle County, Delaware, and each party submits to the
                    exclusive jurisdiction and venue of those courts for
                    purposes of any proceeding. Each party waives any claim that
                    any proceeding brought under section 16.4 has been brought
                    in an inconvenient forum or that the venue of that
                    proceeding is improper.
                  </p>
                  <p className="modern-legal-text">
                    17.5&nbsp;
                    <strong>Recovery of Expenses</strong>
                    <strong>. </strong>In any arbitration or litigation
                    proceedings between the parties arising out of or relating
                    to this agreement, the Prevailing Party will be entitled to
                    recover from the other party, in addition to any other
                    relief awarded, all expenses that the Prevailing Party
                    incurs in those proceedings, including legal fees and
                    expenses. For purposes of this section 16.5, &ldquo;
                    <strong>Prevailing Party</strong>&rdquo; means, for any
                    proceedings, the party in whose favor an award is rendered,
                    except that if in those proceedings the award finds in favor
                    of one party on one or more claims or counterclaims and in
                    favor of the other party on one or more other claims or
                    counterclaims, neither party will be the Prevailing Party.
                    If any proceedings are voluntarily dismissed or are
                    dismissed as part of settlement of that dispute, neither
                    party will be the Prevailing Party in those proceedings.
                  </p>
                  <p className="modern-legal-text">
                    17.6&nbsp;
                    <strong>Jury Trial Waiver. </strong>Each party waives its
                    right to a trial by jury in any proceedings arising out of
                    or relating to this agreement. Either party may enforce this
                    waiver up to and including the first day of trial.
                  </p>
                  <p className="modern-legal-text">
                    17.7&nbsp;
                    <strong>Class Action Waiver. </strong>The parties will
                    conduct all proceedings to resolve a dispute in any forum on
                    an individual basis only. Neither the Creator nor the
                    Company will seek to have any dispute heard as a class
                    action or in any other proceeding in which either party acts
                    or proposes to act in a representative capacity. The parties
                    will not combine any proceeding with another without the
                    advanced written consent of all parties to all affected
                    proceedings.
                  </p>
                  <p className="modern-legal-text">
                    17.8&nbsp;
                    <strong>Limited Time to Bring Claims. </strong>A party will
                    not bring a claim arising out of or relating to this
                    agreement more than one year after the cause of action
                    arose. Any claim brought after one year is barred.
                  </p>
                </div>
              </section>

              <section id="general" className="modern-legal-section">
                <h2 className="modern-legal-section-title">18. General</h2>
                <div className="modern-legal-section-content">
                  <p className="modern-legal-text">
                    18.1&nbsp;
                    <strong>Entire Agreement. </strong>This agreement and the
                    Terms-of-Service Agreement&nbsp; constitute the entire
                    agreement of the parties concerning the subject matter and
                    supersede all earlier written or oral discussions,
                    negotiations, proposals, undertakings, understandings, and
                    agreements between the parties concerning the subject
                    matter. If any conflict or inconsistency exists between this
                    agreement and the{" "}
                    <Link to={ROUTES.LEGAL_TERMS}>
                      Terms-of-Service Agreement
                    </Link>
                    , this agreement will govern.
                  </p>
                  <p className="modern-legal-text">
                    18.2&nbsp;
                    <strong>Amendment. </strong>The Company may change this
                    agreement on one or more occasions, on condition that
                    changes will not apply to ongoing disputes or disputes
                    arising out of events occurring before the posted changes.
                    The Company will notify the Creator by email of any changes
                    to this agreement. Changes will become effective when posted
                    on this page. It is the Creator&rsquo;s responsibility to
                    check this page periodically for changes to this agreement.
                    If the Creator continues to use the Platform after any
                    change, the Company will consider the Creator&rsquo;s
                    continued use of the Platform as acceptance of the change
                    unless the Creator notifies the Company in writing of the
                    Creator&rsquo;s disagreement and the reasons for the
                    Creator&rsquo;s disagreement no later than 15 days after the
                    change. The Company will contact the Creator no later than
                    15 days after receiving the notice to address the notice and
                    try to reach a mutually amicable resolution. If the Company
                    is unable to resolve the Creator&rsquo;s disagreement, the
                    Creator&rsquo;s sole remedy is to terminate this agreement.
                  </p>
                  <p className="modern-legal-text">
                    18.3&nbsp;
                    <strong>Assignment and Delegation</strong>
                    <strong>. </strong>The Creator will not assign any of the
                    Creator&rsquo;s rights or delegate any performance under
                    this agreement, except with the Company&rsquo;s advance
                    written consent. The Company may assign any of its rights or
                    delegate any performance under this agreement without the
                    Creator&rsquo;s consent. Any purported assignment of rights
                    or delegation of performance by the Creator in breach of
                    this section 17.3 is void.
                  </p>
                  <p className="modern-legal-text">
                    18.4&nbsp;
                    <strong>Waivers. </strong>If the Company fails to exercise
                    or enforce any right or provision of this agreement, it will
                    not constitute a waiver of that right or provision. Any
                    waiver of any provision of this agreement will be effective
                    only if in writing and signed by the relevant party.
                  </p>
                  <p className="modern-legal-text">
                    18.5&nbsp;
                    <strong>Severability</strong>
                    <strong>.</strong> If any part of this agreement is declared
                    unenforceable or invalid, the remainder will continue to be
                    valid and enforceable.
                  </p>
                  <p className="modern-legal-text">
                    18.6&nbsp;
                    <strong>Notices</strong>
                  </p>
                  <p className="modern-legal-text">
                    (a)&nbsp;
                    <strong>Notice to the Company. </strong>The Creator may send
                    notice to the Company by email to{" "}
                    <a href={`mailto:${CONFIG.SUPPORT_EMAIL}`}>
                      {CONFIG.SUPPORT_EMAIL}
                    </a>{" "}
                    unless a specific email address is listed on the Platform
                    for giving notice. The Company may change its contact
                    information on one or more occasions by posting the change
                    on the Platform. Please check the Platform for the most
                    current information for sending notice to the Company. The
                    Company will consider an email notice received by it only
                    when the Company&rsquo;s server sends a return message to
                    the Creator acknowledging receipt.
                  </p>
                  <p className="modern-legal-text">
                    (b)&nbsp;
                    <strong>Notice to the Creator. </strong>The Creator consents
                    to receive any notice from the Company in electronic form
                    either (1) by email to the email address listed in the
                    Creator&rsquo;s account or (2) by posting the notice on a
                    place on the Platform chosen for this purpose. The Creator
                    may change the Creator&rsquo;s contact information on one or
                    more occasions by updating the contact information in the
                    Creator&rsquo;s account. The Company will consider notices
                    sent to the Creator by email received when the
                    Company&rsquo;s email service shows transmission to the
                    Creator&rsquo;s email address.
                  </p>
                  <p className="modern-legal-text">
                    18.7&nbsp;
                    <strong>Governing Law. </strong>Delaware law governs all
                    matters arising out of or relating to this agreement,
                    including its validity, interpretation, construction,
                    performance, and enforcement, without giving effect to its
                    conflicts of law principles.
                  </p>
                  <p className="modern-legal-text">
                    18.8&nbsp;
                    <strong>Force Majeure. </strong>The Company is not
                    responsible for any failure to perform if unforeseen
                    circumstances or causes beyond its reasonable control delays
                    or continues to delay its performance, including (a) acts of
                    God, including fire, flood, earthquakes, hurricanes,
                    tropical storms, or other natural disasters; (b) war, riot,
                    arson, embargoes, acts of civil or military authority, or
                    terrorism;{" "}
                    <span style={{ letterSpacing: "-0.1em" }}>
                      (&nbsp; c&nbsp; )
                    </span>{" "}
                    fiber cuts; (d) strikes, or shortages in transportation,
                    facilities, fuel, energy, labor, or materials; (e) failure
                    of the telecommunications or information services
                    infrastructure; and (f) hacking, SPAM, or any failure of a
                    computer, server, network, or software.
                  </p>
                  <p className="modern-legal-text">
                    18.9&nbsp;
                    <strong>No Third-Party Beneficiaries. </strong>
                    This agreement does not, and the parties do not intend it
                    to, confer any rights or remedies on any person other than
                    the parties to this agreement.
                  </p>
                  <p className="modern-legal-text">
                    18.10&nbsp;
                    <strong>Successors and Assigns</strong>
                    <strong>. </strong>This agreement benefits and binds the
                    parties and their respective heirs, successors, and
                    permitted assigns.
                  </p>
                  <p className="modern-legal-text">
                    18.11&nbsp;
                    <strong>Electronic Signature. </strong>The Creator
                    acknowledges that any affirmation, assent, or agreement the
                    Creator sends through the Platform in response to a prompt
                    binds the Creator. The Creator further acknowledges that
                    when the Creator clicks on an &ldquo;I agree,&rdquo;
                    &ldquo;I consent,&rdquo; or other similarly worded
                    &ldquo;button&rdquo; or entry field using a mouse,
                    keystroke, or other computer device, this action is the
                    legal equivalent of the Creator&rsquo;s handwritten
                    signature and binds the Creator in the same way.
                  </p>
                  <p className="modern-legal-text">
                    18.12&nbsp;
                    <strong>Voluntary Agreement. </strong>The Creator has
                    entered into this agreement voluntarily and for valid
                    reasons. The Creator acknowledges that the Creator (i) has
                    carefully read this agreement, (ii) discussed it with the
                    Creator&rsquo;s attorneys or other advisors, (iii)
                    understood all the terms, and (iv) will comply with it. The
                    Creator has relied on the advice of the Creator&rsquo;s
                    attorneys or other advisors about the terms of this
                    agreement and waives any claim that the terms should be
                    construed against the drafter.
                  </p>
                  <p className="modern-legal-text">
                    18.13&nbsp;
                    <strong>No Reliance. </strong>The Creator acknowledges that
                    in electronically signing this agreement, the Creator does
                    not rely and has not relied on any statement by the Company
                    or its agents, except those statements contained in this
                    agreement.
                  </p>
                  <p className="modern-legal-text">
                    18.14&nbsp;
                    <strong>Permission to Send Emails to the Creator. </strong>
                    The Creator grants the Company permission to email notices,
                    advertisements, and other communications to the Creator. The
                    Creator&rsquo;s permission will continue until the Creator
                    asks the Company to remove the Creator from the
                    Company&rsquo;s email list.
                  </p>
                  <p className="modern-legal-text">
                    18.15&nbsp;
                    <strong>Feedback. </strong>The Company encourages the
                    Creator to give feedback about the Company or the Platform.
                    But the Company will not treat as confidential any
                    suggestion or idea the Creator gives, and nothing in this
                    agreement will restrict the Company&rsquo;s right to use,
                    profit from, disclose, publish, or otherwise exploit any
                    feedback, without payment.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default withRouter(ModernContentCreatorAgreement); 